import React, { useState, useRef, useEffect } from 'react';

// ─────────────────────────────────────────────────────────────
// EmailJS config — fill in your own IDs from emailjs.com
// 1. Create free account at emailjs.com
// 2. Add Email Service (Gmail / Outlook) → get SERVICE_ID
// 3. Create Email Template → get TEMPLATE_ID
// 4. Get PUBLIC_KEY from Account → API Keys
// ─────────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = 'service_27fgdeh';   // e.g. 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'template_af8bfeg';  // e.g. 'template_xyz789'
const EMAILJS_PUBLIC_KEY  = 'E-4JCB763uO4KZ-ig';   // e.g. 'user_AbCdEfGhIj'

const SERVICES_LIST = [
  'AI Agent Development',
  'Cloud Consulting (Azure)',
  'Digital Transformation',
  'Mobile / Flutter App',
  'Web App (React / Next.js)',
  '.NET Backend / API',
  'DevSecOps / CI-CD',
  'Internship Enquiry',
  'Other',
];

const BUDGET_OPTIONS = [
  'Under ₹1 Lakh',
  '₹1–5 Lakh',
  '₹5–15 Lakh',
  '₹15–50 Lakh',
  '₹50 Lakh+',
  'Not decided yet',
];

export default function Contact() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    service: '', budget: '', message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  const sectionRef = useRef(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          el.querySelectorAll('.rv, .rl, .rr, .rs').forEach((n) => n.classList.add('on'));
          obs.unobserve(el);
        }
      },
      { threshold: 0.07 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.message.trim() || form.message.length < 20) e.message = 'Please describe your project (min 20 chars)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('sending');

    try {
      // Dynamic import so EmailJS is only loaded when form is submitted
      const emailjs = await import('@emailjs/browser');

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name:    form.name,
          from_email:   form.email,
          phone:        form.phone || 'Not provided',
          company:      form.company || 'Not provided',
          service:      form.service || 'Not specified',
          budget:       form.budget || 'Not specified',
          message:      form.message,
          reply_to:     form.email,
        },
        EMAILJS_PUBLIC_KEY
      );

      setStatus('success');
      setForm({ name: '', email: '', phone: '', company: '', service: '', budget: '', message: '' });
      setTimeout(() => setStatus('idle'), 6000);
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const INPUT_CLASS = 'cf-input';

  return (
    <section className="contact-sec" id="contact" ref={sectionRef} aria-labelledby="contact-heading">
      <div className="ct-wrap">
        {/* Left: info */}
        <div className="rl">
          <div className="chip" style={{ marginBottom: '.8rem' }}>📍 Contact</div>
          <h2
            id="contact-heading"
            style={{ fontSize: 'clamp(1.8rem,2.8vw,2.4rem)', fontWeight: 900, letterSpacing: '-.04em', marginBottom: '.65rem' }}
          >
            Get in Touch
          </h2>
          <p style={{ color: 'var(--body)', lineHeight: 1.8, marginBottom: '2rem' }}>
            We respond within 24 hours. For fastest reply, message us on WhatsApp — our team typically responds within minutes.
          </p>

          <div className="ci-item">
            <div className="ci-ic">📍</div>
            <div>
              <div className="ci-lbl">Office</div>
              <div className="ci-val">SR Complex 36/1c Pkt Road, Thiruchitrambalam<br />Thanjavur, Tamil Nadu 614628, India</div>
            </div>
          </div>
          <div className="ci-item">
            <div className="ci-ic">📞</div>
            <div>
              <div className="ci-lbl">Phone</div>
              <div className="ci-val"><a href="tel:+918123629993">+91 – 81236 29993</a></div>
            </div>
          </div>
          <div className="ci-item">
            <div className="ci-ic">✉️</div>
            <div>
              <div className="ci-lbl">Email</div>
              <div className="ci-val"><a href="mailto:hello@jsdevbrains.com">hello@jsdevbrains.com</a></div>
            </div>
          </div>

          <a
            href="https://wa.me/918123629993"
            target="_blank"
            rel="noopener noreferrer"
            className="wa-contact-btn"
          >
            <div className="wa-c-ic">💬</div>
            <div>
              <div className="wa-c-t">Chat on WhatsApp — Fastest Response</div>
              <div className="wa-c-s">+91 81236 29993 · Replies within minutes</div>
            </div>
          </a>

          <div style={{ fontFamily: "'Poppins',sans-serif", fontSize: '.64rem', fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--muted)', margin: '.6rem 0' }}>
            Follow Us
          </div>
          <div className="soc-row">
            {[
              { label: 'LinkedIn', href: 'https://linkedin.com/company/jsdevbrains', text: 'in' },
              { label: 'X / Twitter', href: '#', text: '𝕏' },
              { label: 'GitHub', href: '#', text: '⌨' },
              { label: 'Instagram', href: '#', text: '📷' },
            ].map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="sc" title={s.label} aria-label={s.label}>
                {s.text}
              </a>
            ))}
          </div>
        </div>

        {/* Right: form */}
        <div className="rr">
          <div className="cf-box">
            <h3 style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 900, fontSize: '1.25rem', marginBottom: '1.4rem', color: 'var(--text)' }}>
              📋 Tell Us About Your Project
            </h3>

            <form onSubmit={handleSubmit} noValidate aria-label="Contact form">
              {/* Name + Email */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
                <div className="fg">
                  <label htmlFor="cf-name">Full Name *</label>
                  <input
                    id="cf-name"
                    name="name"
                    type="text"
                    placeholder="Rajan Kumar"
                    value={form.name}
                    onChange={handleChange}
                    className={INPUT_CLASS}
                    aria-invalid={!!errors.name}
                    autoComplete="name"
                  />
                  {errors.name && <span className="cf-err">{errors.name}</span>}
                </div>
                <div className="fg">
                  <label htmlFor="cf-email">Email Address *</label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={handleChange}
                    className={INPUT_CLASS}
                    aria-invalid={!!errors.email}
                    autoComplete="email"
                  />
                  {errors.email && <span className="cf-err">{errors.email}</span>}
                </div>
              </div>

              {/* Phone + Company */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
                <div className="fg">
                  <label htmlFor="cf-phone">Phone / WhatsApp</label>
                  <input
                    id="cf-phone"
                    name="phone"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={form.phone}
                    onChange={handleChange}
                    className={INPUT_CLASS}
                    autoComplete="tel"
                  />
                </div>
                <div className="fg">
                  <label htmlFor="cf-company">Company / Organisation</label>
                  <input
                    id="cf-company"
                    name="company"
                    type="text"
                    placeholder="Your Company"
                    value={form.company}
                    onChange={handleChange}
                    className={INPUT_CLASS}
                    autoComplete="organization"
                  />
                </div>
              </div>

              {/* Service + Budget */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
                <div className="fg">
                  <label htmlFor="cf-service">Service Needed</label>
                  <select id="cf-service" name="service" value={form.service} onChange={handleChange} className={INPUT_CLASS}>
                    <option value="">Select service…</option>
                    {SERVICES_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="fg">
                  <label htmlFor="cf-budget">Estimated Budget</label>
                  <select id="cf-budget" name="budget" value={form.budget} onChange={handleChange} className={INPUT_CLASS}>
                    <option value="">Select range…</option>
                    {BUDGET_OPTIONS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div className="fg">
                <label htmlFor="cf-msg">Project Description *</label>
                <textarea
                  id="cf-msg"
                  name="message"
                  rows={4}
                  placeholder="Tell us about your project goals, current challenges, and expected timeline…"
                  value={form.message}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                  aria-invalid={!!errors.message}
                />
                {errors.message && <span className="cf-err">{errors.message}</span>}
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="btn-sub"
                disabled={status === 'sending'}
                aria-busy={status === 'sending'}
              >
                {status === 'sending' && <span className="spin">⏳</span>}
                {status === 'idle'    && '🚀 Send Message'}
                {status === 'sending' && 'Sending…'}
                {status === 'success' && '✅ Sent! We\'ll reply within 24 hours'}
                {status === 'error'   && '❌ Failed — try WhatsApp instead'}
              </button>

              {status === 'success' && (
                <p style={{ textAlign: 'center', marginTop: '.75rem', fontSize: '.8rem', color: '#059669' }}>
                  🎉 Thank you for contacting us. Your message has been received, and our team will get back to you shortly.
                </p>
              )}
              {status === 'error' && (
                <p style={{ textAlign: 'center', marginTop: '.75rem', fontSize: '.8rem', color: '#DC2626' }}>
                  Something went wrong. Please{' '}
                  <a href="https://wa.me/918123629993" target="_blank" rel="noopener noreferrer" style={{ color: '#DC2626', textDecoration: 'underline' }}>
                    WhatsApp us directly
                  </a>.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
